import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

// Images
import GoogleCalendarLogo from 'Shared/images/google-calendar.svg';

// Components
import { useConnectCalendar } from 'Shared/utils/hooks/use-connect-calendar';
import SyncMeetingsModal from 'Shared/components/modals/SyncMeetingsModal/SyncMeetingsModal';
import WorkspaceRecurrenceModal from 'Shared/components/modals/WorkspaceRecurrenceModal/WorkspaceRecurrenceModal';
import Outlook365Logo from 'Shared/components/Logos/Outlook365Logo';
import JoinMeetingConferenceButton from 'Shared/components/MeetingConference/JoinMeetingConferenceButton/JoinMeetingConferenceButton';
import { WpButton } from 'Wpds/components/button';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';
import { WpText } from 'Wpds/components/text';
import {
  WpDropdown,
  WpDropdownMenu,
  WpDropdownToggle,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import { WpIcon } from 'Wpds/components/icon';
import UnsyncMeetingModal from './UnsyncMeetingModal/UnsyncMeetingModal';

import './MeetingPanel.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

function MeetingPanel() {
  const userTimeZone = useSelector(state => state.user.person.timezone || 'America/Los_Angeles');
  const { isCalendarConnected, handleConnectGoogleCalendar, handleConnectMicrosoftCalendar } = useConnectCalendar({
    redirectUrl: '/user/integrations',
  });
  const [isChooseMeetingModalOpen, setIsChooseMeetingModalOpen] = useState(false);

  const { isZoom } = useApp();

  const toggleChooseMeetingModalOpen = useCallback(() => setIsChooseMeetingModalOpen(!isChooseMeetingModalOpen), [
    isChooseMeetingModalOpen,
  ]);

  const [isUnsyncMeetingModalOpen, setIsUnsyncMeetingModalOpen] = useState(false);

  const handleUnsyncMeetingModalToggle = useCallback(() => setIsUnsyncMeetingModalOpen(prevState => !prevState), []);

  const recurringMeeting = useSelector(state => state.workspace.recurringMeeting);
  const calendarMeeting = useSelector(state => state.workspace.calendarMeeting);

  const [isWorkspaceRecurrenceModalOpen, setIsWorkspaceRecurrenceModalOpen] = useState(false);

  const workspaceId = useSelector(state => state.workspace.id);
  const workspaceRecurrenceNextRunAt = useSelector(state => state.workspace.recurrence?.nextRunAt);
  const workspaceManualNextEventDate = useSelector(state => state.workspace.manualNextEventDate);

  const meetingConferenceUrl = useSelector(
    state => state.workspace.recurringMeeting?.conferenceUrl || state.workspace.calendarMeeting?.conferenceUrl
  );
  const showJoinMeetingConferenceButton = !!meetingConferenceUrl && !isZoom;

  const toggleWorkspaceRecurrenceModalOpen = useCallback(
    () => setIsWorkspaceRecurrenceModalOpen(!isWorkspaceRecurrenceModalOpen),
    [isWorkspaceRecurrenceModalOpen]
  );

  const handleViewInCalendarClick = () => {
    const htmlLink = recurringMeeting?.nextEventHtmlLink || recurringMeeting?.htmlLink || calendarMeeting?.htmlLink;
    if (htmlLink) window.open(htmlLink, '_blank', 'noopener,noreferrer');
  };

  const syncedObject = (() => {
    if (recurringMeeting) return 'recurring_meeting';
    if (workspaceRecurrenceNextRunAt) return 'recurrence';
    if (workspaceManualNextEventDate) return 'manual_next_event_date';
    if (calendarMeeting) return 'calendar_meeting';
    return null;
  })();

  const nextEventDate = (() => {
    if (recurringMeeting) return recurringMeeting.nextEventDate;
    if (workspaceRecurrenceNextRunAt) return workspaceRecurrenceNextRunAt;
    if (workspaceManualNextEventDate) return workspaceManualNextEventDate;
    if (calendarMeeting && !calendarMeeting.cancelled) return calendarMeeting.startTime;
    return null;
  })();

  const nextMeetingText = (() => {
    if (nextEventDate) return moment(nextEventDate).tz(userTimeZone).format('ddd, MMMM D, h:mma');
    if (syncedObject === 'recurring_meeting' || syncedObject === 'calendar_meeting') return 'Canceled';
    return 'Set time';
  })();

  const syncedToRecurringOrCalendarMeeting =
    syncedObject === 'recurring_meeting' || syncedObject === 'calendar_meeting';

  const baseClassName = 'MeetingPanel';

  return (
    <div className={baseClassName}>
      {isChooseMeetingModalOpen && (
        <SyncMeetingsModal onToggle={toggleChooseMeetingModalOpen} workspaceId={workspaceId} />
      )}
      {isUnsyncMeetingModalOpen && (
        <UnsyncMeetingModal
          isRecurringMeeting={syncedObject === 'recurring_meeting'}
          onToggle={handleUnsyncMeetingModalToggle}
        />
      )}
      <span className={`${baseClassName}__label`}>
        <WpText variant="bodyMd" noMargin color="gray-500" tag="span">
          Next meeting:
        </WpText>
      </span>
      {isWorkspaceRecurrenceModalOpen && (
        <WorkspaceRecurrenceModal onToggle={toggleWorkspaceRecurrenceModalOpen} workspaceId={workspaceId} />
      )}
      <WpDropdown>
        <WpDropdownToggle>
          <WpButton variant="link" qaTest="MeetingPanel-dropdown-toggle">
            {nextMeetingText}
            <WpIcon name="angle-down" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {syncedToRecurringOrCalendarMeeting && isCalendarConnected && (
            <WpDropdownMenuItem onClick={handleViewInCalendarClick}>View In Calendar</WpDropdownMenuItem>
          )}
          {syncedToRecurringOrCalendarMeeting && (
            <WpDropdownMenuItem onClick={handleUnsyncMeetingModalToggle}>Unsync Meeting</WpDropdownMenuItem>
          )}
          {isCalendarConnected && (syncedObject === 'recurrence' || syncedObject === 'manual_next_event_date') && (
            <>
              <WpDropdownMenuItem onClick={toggleWorkspaceRecurrenceModalOpen}>
                {workspaceRecurrenceNextRunAt || workspaceManualNextEventDate ? 'Edit time' : 'Enter time manually'}
              </WpDropdownMenuItem>
              <WpTooltip placement="right">
                <WpTooltipTrigger>
                  <WpDropdownMenuItem onClick={toggleChooseMeetingModalOpen}>
                    Connect to Calendar event
                    <WpIcon name="info-circle" />
                  </WpDropdownMenuItem>
                </WpTooltipTrigger>
                <WpTooltipContent>
                  Connect this workspace to keep reminders relevant and insert a link to the agenda in the calendar
                  invite.
                </WpTooltipContent>
              </WpTooltip>
            </>
          )}

          {(!isCalendarConnected || syncedObject === null) && (
            <>
              <li className={`${baseClassName}__title`}>
                <WpText variant="bodySm" bold noMargin>
                  Connect to Calendar
                </WpText>
                Connect this workspace to keep reminders relevant and insert a link to the agenda in the calendar
                invite.
              </li>
              <WpDropdownMenuItem
                onClick={isCalendarConnected ? toggleChooseMeetingModalOpen : handleConnectGoogleCalendar}
              >
                <div className={`${baseClassName}__option`}>
                  <img src={GoogleCalendarLogo} className={`${baseClassName}__option__logo`} />
                  Google Calendar
                </div>
              </WpDropdownMenuItem>
              <WpDropdownMenuItem
                onClick={isCalendarConnected ? toggleChooseMeetingModalOpen : handleConnectMicrosoftCalendar}
              >
                <div className={`${baseClassName}__option`}>
                  <Outlook365Logo className={`${baseClassName}__option__logo`} />
                  Office365
                </div>
              </WpDropdownMenuItem>
              {(syncedObject === 'recurrence' || syncedObject === null) && (
                <>
                  <WpDropdownMenuDivider />
                  <WpDropdownMenuItem
                    onClick={toggleWorkspaceRecurrenceModalOpen}
                    qaTest="MeetingPanel-manual-time-option"
                  >
                    <WpIcon name="clock" />
                    {workspaceRecurrenceNextRunAt ? 'Edit time' : 'Enter time manually'}
                  </WpDropdownMenuItem>
                </>
              )}
            </>
          )}
          {syncedObject === 'manual_next_event_date' && (
            <>
              <WpDropdownMenuDivider />
              <WpDropdownMenuItem onClick={toggleWorkspaceRecurrenceModalOpen}>Edit time</WpDropdownMenuItem>
            </>
          )}
        </WpDropdownMenu>
      </WpDropdown>
      {showJoinMeetingConferenceButton && (
        <div className="MeetingPanel__join-btn">
          <JoinMeetingConferenceButton variant="tertiary" meetingUrl={meetingConferenceUrl} />
        </div>
      )}
    </div>
  );
}

export default MeetingPanel;
